import React, { useState } from 'react';
import { Container, Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import { ExpandMore, QuestionAnswer } from '@mui/icons-material';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import { useTranslation } from 'react-i18next';

const FAQsPage = () => {
  const { t } = useTranslation();
  const seo = getPageSEO('faqs');
  const [expanded, setExpanded] = useState('faq0');

  const categories = t('website.faqs.categories', { returnObjects: true }) || [];

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <WebsiteLayout>
      <SEO {...seo} />
      {/* Hero Section */}
      <HeroSection chip={t('website.faqs.heroChip')}>
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
              {t('website.faqs.heroTitle')}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.95, color: 'primary.contrastText' }}>
              {t('website.faqs.heroSubtitle')}
            </Typography>
          </Box>
      </HeroSection>

      {/* FAQs */}
      <Container maxWidth="md" sx={{ py: 12 }}>
        {categories.map((category, catIdx) => (
          <Box key={catIdx} sx={{ mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
              {category.title}
            </Typography>

            {category.faqs.map((faq, faqIdx) => {
              const currentIndex = `faq-${catIdx}-${faqIdx}`;
              return (
                <Accordion
                  key={currentIndex}
                  expanded={expanded === currentIndex}
                  onChange={handleChange(currentIndex)}
                  sx={{
                    mb: 2,
                    border: '1px solid',
                    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(22, 51, 0, 0.12)',
                    borderRadius: 2,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'transparent',
                    '&:before': { display: 'none' },
                    boxShadow: expanded === currentIndex ? (theme => theme.palette.mode === 'dark' ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(22, 51, 0, 0.08)') : 'none'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: 'secondary.main' }} />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        my: 2
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        ))}

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8, p: 6, bgcolor: 'primary.main', borderRadius: 4, color: 'primary.contrastText' }}>
          <QuestionAnswer sx={{ fontSize: 48, mb: 2, color: 'primary.contrastText' }} />
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: 'primary.contrastText' }}>
            {t('website.contact.heroSubtitle')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, color: 'primary.contrastText' }}>
            {t('website.contact.form.subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box
              component="a"
              href="/contact"
              sx={{
                px: 4,
                py: 2,
                borderRadius: 2,
                bgcolor: 'primary.contrastText',
                color: 'primary.main',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-block',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(22, 51, 0, 0.3)',
                  bgcolor: 'secondary.dark'
                }
              }}
            >
              {t('website.common.contactSupport')}
            </Box>
            <Box
              component="a"
              href="/demo"
              sx={{
                px: 4,
                py: 2,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'primary.contrastText',
                color: 'primary.contrastText',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-block',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: 'rgba(22, 51, 0, 0.05)',
                  borderColor: 'primary.contrastText'
                }
              }}
            >
              {t('website.common.scheduleDemo')}
            </Box>
          </Box>
        </Box>
      </Container>
    </WebsiteLayout>
  );
};

export default FAQsPage;
